/**
 * Per-product detail of a bank (ADR-0047, CP-1 `ProductLine`): a pure read of the books and the `ProductDef`s, with the period's flows when its postings are given.
 * Balances are the books' (amortized cost; an AFS line adds its fair value and the unrealised mark). Repricing buckets, duration and the deposit betas follow 05.5 E1–E2
 * and 01.3 (`steps/irr.ts` reads the same classes). A posting names its product only through its vintage, and a book-level flow (runoff, charge-offs, the book's interest)
 * names none, so a flow with no vintage is spread over the book's products by their balance (interest: by balance × coupon).
 * The spread is a display allocation, not a posting; the ledger's amounts are the book totals.
 */
import { cumulativeDepositBeta, minPolicyMoveRateOf } from "./deposit-beta";
import {
  AFS_BOOKS, ASSET_BOOKS, DEPOSIT_BOOKS, HTM_BOOKS, LIABILITY_BOOKS, LOAN_BOOKS, WHOLESALE_BOOKS,
  type BankState, type BookId, type EngineContext, type EnvSnapshot, type Posting, type ProductDef, type ProductId, type ProductLine, type Rate, type Vintage,
} from "@bsim/domain";
import { archetypeInputs, baselineEnvOf } from "./archetype-inputs";
import { bookChangeUsd } from "./kpi-inputs";

type Buckets = ProductLine["repricingUsd"];
export const REPRICING_BUCKETS = ["m0to3", "m3to12", "y1to3", "y3to5", "y5plus", "nonSensitive"] as const satisfies readonly (keyof Buckets)[];
const NON_SENSITIVE = REPRICING_BUCKETS.length - 1;

/** The bucket an amount `months` from repricing falls in (05.5 E2's table: up to 3 months, to 12, to 3 years, to 5, beyond). */
export const bucketOfMonths = (months: number): number => (months <= 3 ? 0 : months <= 12 ? 1 : months <= 36 ? 2 : months <= 60 ? 3 : 4);

/**
 * The repricing bucket of a vintage: months to the earlier of its next reset and maturity; a non-maturity deposit by its behavioural average life; a floating or
 * administered rate with no date reprices at once; a fixed rate with no date is not rate sensitive.
 */
export function repricingBucketOf(v: Vintage, product: ProductDef, month: number): number {
  if (product.nmdAverageLifeYears !== undefined) return bucketOfMonths(product.nmdAverageLifeYears * 12);
  const dates = [v.nextResetMonth, v.maturityMonth].filter((m): m is NonNullable<typeof m> => m !== undefined);
  if (dates.length > 0) return bucketOfMonths(Math.min(...dates) - month);
  return product.rateType === "fixed" ? NON_SENSITIVE : 0;
}

const isIn = (books: readonly BookId[], book: BookId): boolean => books.includes(book);

/** SimStart FF: the month-0 keyframe every scenario shares (01.1 §3). */
export const startFfRateOf = (ctx: EngineContext): Rate => baselineEnvOf(ctx, (ctx.content.scenarios[0] as NonNullable<EngineContext["content"]["scenarios"][number]>).id).ffRate;

/** One product's rows of the book, summed once so every field reads the same vintages. */
interface Sums {
  balanceUsd: number; fairValueUsd: number; couponUsd: number; durationUsd: number; durationBalanceUsd: number; fixedUsd: number; uninsuredUsd: number; aclUsd: number; boxUsd: number; boxBalanceUsd: number;
  buckets: Buckets; matures: { d0to30: number; m1to12: number; later: number }; pledgedUsd: number;
}

function sumsOf(vintages: readonly Vintage[], product: ProductDef, month: number): Sums {
  const s: Sums = {
    balanceUsd: 0, fairValueUsd: 0, couponUsd: 0, durationUsd: 0, durationBalanceUsd: 0, fixedUsd: 0, uninsuredUsd: 0, aclUsd: 0, boxUsd: 0, boxBalanceUsd: 0,
    buckets: { m0to3: 0, m3to12: 0, y1to3: 0, y3to5: 0, y5plus: 0, nonSensitive: 0 }, matures: { d0to30: 0, m1to12: 0, later: 0 }, pledgedUsd: 0,
  };
  const fixedType = product.rateType === "fixed" || product.rateType === "hybrid";
  for (const v of vintages) {
    if (v.productId !== product.id) continue;
    const b = v.balanceUsd, fv = v.fairValueUsd ?? b;
    s.balanceUsd += b; s.fairValueUsd += fv; s.couponUsd += b * v.couponRate; s.pledgedUsd += v.pledgedUsd ?? 0; s.aclUsd += v.aclUsd ?? 0;
    if (fixedType) s.fixedUsd += b;
    s.uninsuredUsd += v.uninsuredUsd ?? b * (product.uninsuredShareRate ?? 0);
    const duration = v.durationYears ?? product.eve?.durationYears;
    if (duration !== undefined) { s.durationUsd += fv * duration; s.durationBalanceUsd += fv; }
    if (v.underwritingBox !== undefined) { s.boxUsd += b * v.underwritingBox; s.boxBalanceUsd += b; }
    s.buckets[REPRICING_BUCKETS[repricingBucketOf(v, product, month)] as keyof Buckets] += b;
    const left = v.maturityMonth === undefined ? 0 : v.maturityMonth - month;
    s.matures[left <= 1 ? "d0to30" : left <= 12 ? "m1to12" : "later"] += b;
  }
  return s;
}

/** book → product (`undefined`: the flow names none) → usd. */
type Rows = ReadonlyMap<BookId, Map<ProductId | undefined, number>>;

/** A period's flows by book, and by product where a posting names a vintage. */
interface PeriodFlows {
  readonly months: number;
  readonly change: Partial<Record<BookId, number>>;
  readonly by: (select: (p: Posting) => number) => Rows;
}

function flowsOf(postings: readonly Posting[], months: number, productOf: ReadonlyMap<string, ProductId>): PeriodFlows {
  return {
    months, change: bookChangeUsd(postings),
    by: (select) => {
      const out = new Map<BookId, Map<ProductId | undefined, number>>();
      for (const p of postings) {
        const usd = select(p);
        if (usd === 0 || p.bookId === undefined) continue;
        const rows = out.get(p.bookId) ?? new Map<ProductId | undefined, number>();
        const product = p.vintageId === undefined ? undefined : productOf.get(p.vintageId);
        rows.set(product, (rows.get(product) ?? 0) + usd);
        out.set(p.bookId, rows);
      }
      return out;
    },
  };
}

const INCOME = "income:interest.", EXPENSE = "expense:interest.";
/** A book's interest: income lines for assets (credit +), expense lines for liabilities (debit +). */
const interestOf = (p: Posting): number =>
  p.credit.startsWith(INCOME) ? p.amountUsd : p.debit.startsWith(INCOME) ? -p.amountUsd : p.debit.startsWith(EXPENSE) ? p.amountUsd : p.credit.startsWith(EXPENSE) ? -p.amountUsd : 0;

/**
 * `bookDetails(bank, ctx, period?, env?)`: every product's line. `period` adds the flows of a span of `months` (interest, yield, originations, runoff, charge-offs,
 * net deposit flow); without it the optional ones (originations, runoff, charge-offs, net flow) are absent, not 0, and the yield is the coupon. `interestUsd` is absent, not 0, without a period (no reading). `env` adds the realised deposit beta (the move in each posted rate since SimStart over the
 * move in FF).
 */
export function bookDetails(bank: BankState, ctx: EngineContext, period?: { readonly postings: readonly Posting[]; readonly months: number }, env?: EnvSnapshot): readonly ProductLine[] {
  const inputs = archetypeInputs(ctx, bank.archetypeId);
  const { month } = bank;
  const productOf = new Map<string, ProductId>();
  for (const id of [...ASSET_BOOKS, ...LIABILITY_BOOKS]) for (const v of bank.books[id].vintages) productOf.set(v.id, v.productId);
  const flows = period === undefined ? undefined : flowsOf(period.postings, period.months, productOf);
  // Book-level flows, spread over the book's products where no vintage names one.
  const interest = flows?.by(interestOf);
  const originated = flows?.by((p) => (p.driver === "origination" ? p.amountUsd : 0));
  const runoff = flows?.by((p) => (p.driver === "paydown" || p.driver === "prepay" || p.driver === "maturity" ? p.amountUsd : 0));
  const chargedOff = flows?.by((p) => (p.driver === "chargeOff" ? p.amountUsd : p.driver === "recovery" ? -p.amountUsd : 0));
  const netFlow = flows?.by((p) => (p.debit.startsWith("liab:") ? -p.amountUsd : p.credit.startsWith("liab:") ? p.amountUsd : 0));
  const ffChange = env === undefined ? undefined : env.ffRate - startFfRateOf(ctx);
  const minMove = minPolicyMoveRateOf(ctx);

  const sums = new Map<ProductId, Sums>();
  for (const p of inputs.products) sums.set(p.id, sumsOf(bank.books[p.bookId].vintages, p, month));
  const bookTotal = (book: BookId, of: (s: Sums) => number): number => inputs.products.reduce((t, p) => (p.bookId === book ? t + of(sums.get(p.id) as Sums) : t), 0);

  /** `usd` of `rows` for the product: what names it, plus its share of what names none. */
  const share = (rows: Rows | undefined, p: ProductDef, weight: (s: Sums) => number): number => {
    const book = rows?.get(p.bookId);
    if (book === undefined) return 0;
    const total = bookTotal(p.bookId, weight);
    return (book.get(p.id) ?? 0) + (total === 0 ? 0 : ((book.get(undefined) ?? 0) * weight(sums.get(p.id) as Sums)) / total);
  };
  const balanceWeight = (s: Sums): number => s.balanceUsd;
  const couponWeight = (s: Sums): number => s.couponUsd;

  return inputs.products.map((p): ProductLine => {
    const s = sums.get(p.id) as Sums;
    const securities = isIn(AFS_BOOKS, p.bookId) || isIn(HTM_BOOKS, p.bookId);
    const asset = isIn(ASSET_BOOKS, p.bookId);
    const couponRate = (s.balanceUsd === 0 ? 0 : s.couponUsd / s.balanceUsd) as Rate;
    const bookInterest = [...(interest?.get(p.bookId)?.values() ?? [])].reduce((t, usd) => t + usd, 0);
    const interestUsd = flows === undefined ? undefined : Math.round(bookTotal(p.bookId, couponWeight) === 0 ? 0 : (bookInterest * s.couponUsd) / bookTotal(p.bookId, couponWeight));
    // The mean balance of the span: the book's opening (closing less the postings' change) and closing, at this product's share of the book.
    const bookClosing = bookTotal(p.bookId, balanceWeight);
    const meanUsd = flows === undefined || bookClosing === 0 ? s.balanceUsd : s.balanceUsd * (1 - (flows.change[p.bookId] ?? 0) / bookClosing / 2);
    const yieldRate = (flows === undefined || interestUsd === undefined || meanUsd <= 0 ? couponRate : (interestUsd * 12) / flows.months / meanUsd) as Rate;
    const rateType = p.rateType === "hybrid" ? "fixed" : p.rateType;
    const line: ProductLine = {
      productId: p.id, bookId: p.bookId, label: p.label, side: asset ? "asset" : "liability",
      balanceUsd: s.balanceUsd, rateType, couponRate, yieldRate, ...(interestUsd === undefined ? {} : { interestUsd }), fixedShareRate: (s.balanceUsd === 0 ? 0 : s.fixedUsd / s.balanceUsd) as Rate, repricingUsd: s.buckets,
      ...(s.durationBalanceUsd > 0 ? { durationYears: s.durationUsd / s.durationBalanceUsd } : {}),
    };
    if (securities) Object.assign(line, { fairValueUsd: s.fairValueUsd, unrealizedUsd: s.fairValueUsd - s.balanceUsd, accounting: isIn(AFS_BOOKS, p.bookId) ? "AFS" : "HTM" });
    if (isIn(LOAN_BOOKS, p.bookId)) {
      const credit = bank.credit?.[p.bookId];
      const ofBook = (usd: number): number => (bookClosing === 0 ? 0 : (usd * s.balanceUsd) / bookClosing);
      Object.assign(line, {
        // The span's flows exist only when a period is given: without one they are absent, not 0.
        ...(flows === undefined ? {} : { originationsUsd: share(originated, p, balanceWeight), runoffUsd: share(runoff, p, balanceWeight), ncoUsd: share(chargedOff, p, balanceWeight) }),
        aclUsd: credit === undefined ? s.aclUsd : ofBook(credit.aclUsd), nonaccrualUsd: credit === undefined ? 0 : ofBook(credit.nonaccrualUsd),
        ...(s.boxBalanceUsd > 0 ? { boxNotch: Math.round(s.boxUsd / s.boxBalanceUsd) } : {}),
      });
    }
    if (isIn(DEPOSIT_BOOKS, p.bookId)) {
      const beta = inputs.refs[inputs.productIndex.get(p.id) as number]?.depositBeta?.params.betaThroughCycle;
      const start = inputs.ibDepositStart.get(p.id);
      Object.assign(line, {
        ...(beta === undefined ? {} : { betaRate: beta }),
        // Too little policy move yet: nothing has been passed through, so the realised beta is undefined, not 0 (the one definition, `cumulativeDepositBeta`).
        ...(start === undefined || ffChange === undefined || cumulativeDepositBeta(couponRate - start.couponRate, ffChange, minMove) === undefined ? {} : { realizedBetaRate: cumulativeDepositBeta(couponRate - start.couponRate, ffChange, minMove) as number }),
        uninsuredUsd: s.uninsuredUsd, uninsuredShareRate: s.balanceUsd === 0 ? 0 : s.uninsuredUsd / s.balanceUsd,
        ...(p.inRunScope === undefined ? {} : { inRunScope: p.inRunScope }), ...(p.nmdAverageLifeYears === undefined ? {} : { averageLifeYears: p.nmdAverageLifeYears }),
        ...(flows === undefined ? {} : { netFlowUsd: share(netFlow, p, balanceWeight) }),
      });
    }
    if (isIn(WHOLESALE_BOOKS, p.bookId)) {
      const { liquidity } = bank;
      const capacityUsd = p.bookId === "fhlb" ? liquidity.fhlbCapacityUsd : p.bookId === "dw" ? liquidity.dwCapacityUsd : p.bookId === "ffp" ? liquidity.ffpLineUsd : undefined;
      Object.assign(line, { maturesUsd: s.matures, ...(capacityUsd === undefined ? {} : { capacityUsd }), ...(p.bookId === "fhlb" ? { pledgedUsd: pledgedSecuritiesUsd(bank) } : {}) });
    }
    return line;
  });
}

/** The securities' pledged fair value (06.2 E2), which backs the FHLB line. The pool is one collateral base, shown on the FHLB line. */
const pledgedSecuritiesUsd = (bank: BankState): number => [...AFS_BOOKS, ...HTM_BOOKS].reduce((t, b) => t + bank.books[b].vintages.reduce((u, v) => u + (v.pledgedUsd ?? 0), 0), 0);
